import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { apiEvents } from '../lib/apiEvents.js';
import { isNonStaffArea } from '../lib/nonStaffAreas.js';
import { hadSession } from '../lib/sessionMarker.js';

export function ApiAuthGuard(): null {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    const off = apiEvents.on('unauthenticated', () => {
      queryClient.setQueryData(['auth', 'me'], null);

      // NEVER from a shop-floor kiosk.
      //
      // Everything under /scan identifies people by badge and has no session
      // by design — the saw, the worker station, the laser, goods issue,
      // remnants, the stock count, the NC kiosk. A 401 there is the normal
      // state, not a problem: /api/auth/me answers 401 on every load because
      // nobody is logged in, which is the point.
      //
      // This guard sat at the app root and redirected on ANY 401 from ANY
      // path, so every one of those screens bounced to a staff login the
      // operator cannot pass. Eli reported it as "the saw station won't open,
      // there's a network error"; measured, it was all seven of them, on both
      // production hosts. The screen loaded, /api/auth/me answered 401 as it
      // always had, and the guard threw the operator out.
      //
      // A staff screen still needs the redirect — a session that expires
      // overnight should land on the login page rather than an empty screen.
      // So the rule is by area, not by status code.
      //
      // 2026-08-28: written out as a list rather than one path, because the
      // shop floor was never the only area with its own door. /portal is the
      // customer's login and /field is the installation crew's job code, and
      // neither of those people can pass a staff login either.
      //
      // 2026-09-01: the list moved to `lib/nonStaffAreas.ts`, because
      // `saveErrors.ts` was silently assuming the opposite of what happens on
      // this line and could not see it. A new door added later is added there,
      // once, and both readers get it.
      if (isNonStaffArea(location.pathname)) return;

      if (!location.pathname.startsWith('/app/login')) {
        // UX X1 (אלי 30/10/2026): הכתובת המלאה — `?project=…&tab=…` — כדי
        // לחזור לאותו מסך בדיוק, ו-`expired` כדי שמסך הכניסה יאמר שהסשן פג.
        // ⚠ לא `true` קבוע: גם `/api/auth/me` של מי שמעולם לא נכנס עונה 401
        // ומגיע לכאן — „פגה" רק למי שהיה מחובר ולא יצא.
        navigate('/app/login', {
          state: { from: location.pathname + location.search + location.hash, expired: hadSession() },
          replace: true,
        });
      }
    });
    return off;
  }, [navigate, location, queryClient]);

  return null;
}
